/**
 * IXVerify v2 — in-app report preview.
 *
 * Every surface that offered the report used to presign it and `window.open`
 * the URL, and the presign forced `Content-Disposition: attachment` — so
 * "view the report" was really "download the report, then find it and open
 * it". This renders the sealed PDF in place instead.
 *
 * The bytes are NOT proxied: the URL is the same on-demand presign
 * (`GET /sessions/:id/report-url`, audited as `ixv_report_presigned`, never
 * stored — the v1 expiring-stored-URL defect), signed with
 * `?disposition=inline` so the browser's native PDF viewer renders it inside
 * the iframe with its own zoom/page/print/save controls. Cross-origin bytes in
 * an iframe need no CORS grant on the bucket, so this works against GCS and
 * the S3 fallback alike.
 *
 * The preview NEVER replaces the escape hatches — mobile browsers (iOS Safari
 * in particular) render only the first page of an embedded PDF, so "Open" and
 * "Download" are always present, not a fallback that has to be triggered.
 * Download re-presigns with the attachment disposition rather than reusing the
 * inline URL, so the save path is unchanged from before.
 *
 * R3: the report carries the subject's DOB. The presigned URL is short-lived
 * and stays in this component — never logged, never put in app routing.
 */
import { useCallback, useEffect, useState } from 'react';
import { Download, ExternalLink, FileWarning, Loader2 } from 'lucide-react';
import { Modal, primaryBtnCls, secondaryBtnCls } from '../../../ui';
import { getReportUrl, errorMessage } from '../../lib/api';

export default function ReportPreview({
  sessionId,
  verificationCode = null,
  onClose,
}: {
  sessionId: string;
  /** Shown in the title so a reviewer can tell which dispense they opened. */
  verificationCode?: string | null;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [revision, setRevision] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setUrl(null);
    try {
      const res = await getReportUrl(sessionId, { inline: true });
      setUrl(res.url);
      setRevision(res.revision);
    } catch (err) {
      setError(errorMessage(err, 'Could not open the report'));
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The download presign is separate (attachment disposition) so the browser
  // saves the file instead of navigating the tab to a rendered PDF.
  const download = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await getReportUrl(sessionId);
      window.open(res.url, '_blank', 'noopener');
    } catch (err) {
      setError(errorMessage(err, 'Could not download the report'));
    } finally {
      setDownloading(false);
    }
  }, [sessionId]);

  const title = verificationCode ? `Report · ${verificationCode}` : 'Verification report';

  return (
    <Modal isOpen title={title} onClose={onClose} maxWidth="max-w-5xl">
      <div className="space-y-3">
        <div className="h-[70vh] min-h-[320px] overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
          {error ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
              <FileWarning className="h-8 w-8 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              <p className="text-sm text-gray-600 dark:text-gray-300">{error}</p>
              <button type="button" className={secondaryBtnCls} onClick={() => void load()}>
                Try again
              </button>
            </div>
          ) : url ? (
            <iframe
              src={url}
              title={title}
              className="h-full w-full border-0"
              data-testid="ixv-report-frame"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Loader2
                className="h-6 w-6 animate-spin text-primary-600 motion-reduce:animate-none dark:text-primary-400"
                aria-hidden="true"
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {revision != null ? `Revision ${revision}` : ' '}
            {revision != null && revision > 1 ? ' — the countersigned copy' : ''}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={secondaryBtnCls}
              disabled={!url}
              onClick={() => url && window.open(url, '_blank', 'noopener')}
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" /> Open in new tab
            </button>
            <button type="button" className={primaryBtnCls} disabled={downloading} onClick={() => void download()}>
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : (
                <Download className="h-4 w-4" aria-hidden="true" />
              )}
              Download
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
